// Pure helpers for the daily-cycle screens (owner: cycle work package).
// No React, no DOM and relative imports only, so tests/unit can import this file
// under the node tsconfig and vitest without the '@/' alias.
//
// Nothing here decides whether work may proceed or whether a step will pass: the
// server does that. These helpers pick defaults, find records in the bundle and check
// that a form is filled in before it is sent.
import { GAS_SPECIES } from '../../domain/catalogue'
import type {
  AvailableAction,
  CommandArgs,
  CycleView,
  GasTestView,
  PermitBundle,
  PolicyView,
  RevisionRequirement,
  RevisionView,
  SuspensionView,
  ToolboxTalkView,
} from '../../domain/types'

export const MINUTE_MS = 60_000
export const HOUR_MS = 60 * MINUTE_MS

/** Default length of a requested cycle (task brief: now -> now + 10 h). */
export const DEFAULT_CYCLE_HOURS = 10
/**
 * Used only when the policy read is unavailable, for a warning (never to block).
 * The policy value itself is SP-2329's "normally 12 hours", pending BadrEOR approval.
 */
export const FALLBACK_NORMAL_VALIDATION_HOURS = 12

export function floorToMinute(ms: number): number {
  return Math.floor(ms / MINUTE_MS) * MINUTE_MS
}

export function ceilToMinute(ms: number): number {
  return Math.ceil(ms / MINUTE_MS) * MINUTE_MS
}

export function parseIsoMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

export function msToIso(ms: number): string {
  return new Date(ms).toISOString()
}

/**
 * Best estimate of the server clock: the local clock corrected by the offset seen when
 * the permit bundle was read (server generated_at versus local receive time). Used only
 * for form defaults; the server stamps every signature with its own clock.
 */
export function serverNowMs(generatedAt: string | null | undefined, receivedAtMs: number | null | undefined, localNowMs: number): number {
  const generated = parseIsoMs(generatedAt)
  if (generated === null || !receivedAtMs || !Number.isFinite(receivedAtMs)) return localNowMs
  return localNowMs + (generated - receivedAtMs)
}

export type TimeWindow = { from: number; to: number }

/**
 * request_cycle default: now -> now + 10 h, kept inside the authorisation window so the
 * default never proposes time the RS did not authorise. Whole minutes, because the
 * datetime inputs have minute precision: the start is rounded up and a clamped end is
 * rounded down, so both stay inside the window.
 */
export function defaultCycleWindow(
  nowMs: number,
  authorisation: { authorisation_from: string; authorisation_to: string } | null,
  hours: number = DEFAULT_CYCLE_HOURS,
): TimeWindow {
  let from = ceilToMinute(nowMs)
  const authFrom = parseIsoMs(authorisation?.authorisation_from)
  if (authFrom !== null && authFrom > from) from = ceilToMinute(authFrom)
  const unclamped = from + hours * HOUR_MS
  let to = unclamped
  const authTo = parseIsoMs(authorisation?.authorisation_to)
  if (authTo !== null && authTo < to) to = floorToMinute(authTo)
  // Authorisation already over: keep a sensible window and let the server explain.
  if (to <= from) to = unclamped
  return { from, to }
}

/**
 * validate_cycle default: the cycle's requested window, starting no earlier than now
 * (the server refuses a validation start more than 15 minutes in the past).
 */
export function defaultValidationWindow(nowMs: number, cycle: Pick<CycleView, 'requested_from' | 'requested_to'>): TimeWindow {
  const requestedFrom = parseIsoMs(cycle.requested_from) ?? nowMs
  const requestedTo = parseIsoMs(cycle.requested_to) ?? requestedFrom
  const from = Math.max(ceilToMinute(requestedFrom), floorToMinute(nowMs))
  const to = floorToMinute(requestedTo)
  return { from, to }
}

/** validate_cycle refuses a validated period that starts more than this long ago (CORE-056). */
export const VALIDATION_MAX_BACKDATE_MINUTES = 15
/**
 * The default start goes back to the cycle start only while that leaves this much of the
 * 15 minutes for the RS to answer the checks and sign.
 */
export const VALIDATION_DEFAULT_BACKDATE_MINUTES = 10

/**
 * Default "Valid from" (exact ms): the cycle's requested start when the server will still
 * accept it (in the future, or at most VALIDATION_DEFAULT_BACKDATE_MINUTES ago), else now.
 * Starting at the cycle start keeps a gas test the AGT took after the cycle request inside
 * the validated period; for PH acceptance the test must be observed inside that period.
 */
export function defaultValidationStart(nowMs: number, requestedFromIso: string | null | undefined): number {
  const requestedFrom = parseIsoMs(requestedFromIso)
  if (requestedFrom === null) return nowMs
  return requestedFrom >= nowMs - VALIDATION_DEFAULT_BACKDATE_MINUTES * MINUTE_MS ? requestedFrom : nowMs
}

/** The earliest validation start the server accepts at `nowMs`: the cycle start, or 15 minutes ago if later. */
export function earliestValidationStart(nowMs: number, requestedFromIso: string | null | undefined): number {
  const floor = nowMs - VALIDATION_MAX_BACKDATE_MINUTES * MINUTE_MS
  const requestedFrom = parseIsoMs(requestedFromIso)
  return requestedFrom === null ? floor : Math.max(floor, requestedFrom)
}

/**
 * Whether a gas test was observed inside a validated period (inclusive). For PH acceptance
 * the server counts only such a test: one taken before the period does not count for
 * starting work. Null when a time is missing.
 */
export function observedInPeriod(
  test: Pick<GasTestView, 'reported_observed_at'>,
  period: { validation_from: string | null | undefined; validation_to: string | null | undefined },
): boolean | null {
  const at = parseIsoMs(test.reported_observed_at)
  const from = parseIsoMs(period.validation_from)
  const to = parseIsoMs(period.validation_to)
  if (at === null || from === null || to === null) return null
  return at >= from && at <= to
}

/** Cycle states in which work is stopped; a gas test may still be recorded there, for the record only. */
export function isStoppedCycle(state: CycleView['cycle_state'] | string | null | undefined): boolean {
  return state === 'suspension_reported' || state === 'suspended'
}

export function minutesBetween(fromIso: string | null | undefined, toIso: string | null | undefined): number | null {
  const from = parseIsoMs(fromIso)
  const to = parseIsoMs(toIso)
  if (from === null || to === null) return null
  return Math.round((to - from) / MINUTE_MS)
}

/** "10 h", "10 h 30 min", "45 min", "21 days 6 h" (from 48 h); negative spans read "-1 h 5 min". */
export function formatDuration(minutes: number): string {
  const sign = minutes < 0 ? '-' : ''
  const m = Math.abs(Math.round(minutes))
  const h = Math.floor(m / 60)
  const rest = m % 60
  if (h >= 48) {
    const days = Math.floor(h / 24)
    const hours = h % 24
    return hours === 0 ? `${sign}${days} days` : `${sign}${days} days ${hours} h`
  }
  if (h === 0) return `${sign}${rest} min`
  return rest === 0 ? `${sign}${h} h` : `${sign}${h} h ${rest} min`
}

// -----------------------------------------------------------------------------
// Policy values (always shown with their status; never presented as approved)
// -----------------------------------------------------------------------------
export type PolicyParameter = PolicyView['parameters'][number]

export function policyParameter(policy: PolicyView | null | undefined, code: string): PolicyParameter | null {
  return policy?.parameters.find((p) => p.parameter_code === code) ?? null
}

export function isUsable(p: PolicyParameter | null): p is PolicyParameter {
  return p !== null && p.value_status !== 'unresolved' && p.value !== null && p.value !== undefined
}

export function usableNumber(p: PolicyParameter | null): number | null {
  if (!isUsable(p)) return null
  const v = typeof p.value === 'string' ? Number(p.value) : p.value
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function usableText(p: PolicyParameter | null): string | null {
  if (!isUsable(p)) return null
  return typeof p.value === 'string' && p.value.trim() ? p.value : null
}

/** gas.species_units as {species: [unit, ...]}; null when unresolved or malformed. */
export function allowedUnits(p: PolicyParameter | null): Record<string, string[]> | null {
  if (!isUsable(p) || typeof p.value !== 'object' || Array.isArray(p.value)) return null
  const out: Record<string, string[]> = {}
  for (const [species, units] of Object.entries(p.value as Record<string, unknown>)) {
    if (Array.isArray(units)) out[species] = units.filter((u): u is string => typeof u === 'string' && u.length > 0)
  }
  return out
}

export const VALUE_STATUS_TEXT: Record<PolicyParameter['value_status'], string> = {
  approved: 'approved rule value',
  source_confirmed_pending_approval: 'SP-2329 reference value, not yet approved by BadrEOR',
  synthetic_fixture: 'synthetic demo value, not approved policy',
  unresolved: 'unresolved: no value is set',
}

// -----------------------------------------------------------------------------
// Finding records in the bundle
// -----------------------------------------------------------------------------
export function currentAuthorisation(bundle: PermitBundle): PermitBundle['authorisations'][number] | null {
  return bundle.authorisations.find((a) => a.id === bundle.permit.current_authorisation_id) ?? null
}

export function sortCyclesNewestFirst(cycles: readonly CycleView[]): CycleView[] {
  return [...cycles].sort((a, b) => b.cycle_number - a.cycle_number)
}

export function findCycle(bundle: PermitBundle, id: string | null | undefined): CycleView | null {
  if (!id) return null
  return bundle.cycles.find((c) => c.id === id) ?? null
}

/** A string argument hint from the server's available action (e.g. cycle_id). */
export function actionArg(action: Pick<AvailableAction, 'args'>, key: string): string | null {
  const v = action.args?.[key]
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** The cycle an action is about: the server's hint, else the permit's current cycle. */
export function cycleForAction(bundle: PermitBundle, action: Pick<AvailableAction, 'args'>): CycleView | null {
  return findCycle(bundle, actionArg(action, 'cycle_id') ?? bundle.permit.current_cycle_id)
}

export function revisionOf(bundle: PermitBundle, cycle: Pick<CycleView, 'revision_id'> | null): RevisionView | null {
  const id = cycle?.revision_id ?? bundle.permit.current_revision_id
  return bundle.revisions.find((r) => r.id === id) ?? null
}

export type GasPlan = Extract<RevisionRequirement, { requirement_kind: 'gas_testing' }>['detail']

export function gasPlanOf(revision: RevisionView | null | undefined): GasPlan | null {
  for (const r of revision?.requirements ?? []) {
    if (r.requirement_kind === 'gas_testing') return r.detail
  }
  return null
}

/** The sealed revision asks for the first-day worksite examination (exam kind aa_first_day, done by the RS from 0016) and this is cycle 1. */
export function requiresFirstDayExam(revision: RevisionView | null | undefined, cycle: Pick<CycleView, 'cycle_number'>): boolean {
  const required = (revision?.requirements ?? []).some(
    (r) => r.requirement_kind === 'worksite_examination' && r.detail.exam_kind === 'aa_first_day',
  )
  return required && cycle.cycle_number === 1
}

/** What a Class A validation needs from the validating RS (0030). Null for other classes. */
export type ClassAValidationNeeds = {
  /** Worksite examination at this validation (Class A, every day where the rules say so). */
  dailyExam: boolean
  /** The hydrocarbon-area rule is undecided (CORE-069): the server refuses the validation. */
  hcaUnresolved: boolean
  /** The examination rule itself is not usable under the rules in use. */
  examRuleBlocked: boolean
  /** Who does (or did) the first gas test of this cycle, when it is someone other than the viewer. */
  otherFirstTester: { id: string; name: string } | null
  /** Whether the rules let the validating RS delegate the first test; null when not usable. */
  delegationAllowed: boolean | null
}

/**
 * Class A conditions for validating `cycle` (CORE-035, CORE-039, CORE-055). The first gas
 * test is the earliest recorded test of the cycle, or the pre-arranged tester when none is
 * recorded yet. The server decides; this only shapes the form.
 */
export function classAValidationNeeds(
  bundle: Pick<PermitBundle, 'permit' | 'class_a' | 'viewer'>,
  cycle: Pick<CycleView, 'gas_tests'>,
  prearrangedTester: { id: string; name: string } | null,
): ClassAValidationNeeds | null {
  if (bundle.permit.permit_class !== 'A' || !bundle.class_a) return null
  const ca = bundle.class_a
  const first = [...cycle.gas_tests].sort((a, b) => (parseIsoMs(a.recorded_at) ?? 0) - (parseIsoMs(b.recorded_at) ?? 0))[0] ?? null
  const tester = first ? { id: first.attestation.signer.id, name: first.attestation.signer.display_name } : prearrangedTester
  const me = bundle.viewer.person?.id ?? null
  return {
    dailyExam: ca.daily_examination === 'required',
    hcaUnresolved: ca.daily_examination === 'unresolved',
    examRuleBlocked: ca.daily_examination === 'policy_blocked',
    otherFirstTester: tester && tester.id !== me ? tester : null,
    delegationAllowed: ca.first_test_delegation_allowed,
  }
}

export function predecessorOf(bundle: PermitBundle, cycle: Pick<CycleView, 'replaces_cycle_id'>): CycleView | null {
  return findCycle(bundle, cycle.replaces_cycle_id)
}

function recordedMs(t: GasTestView): number {
  return parseIsoMs(t.recorded_at) ?? 0
}

export function testsNewestFirst(tests: readonly GasTestView[]): GasTestView[] {
  return [...tests].sort((a, b) => recordedMs(b) - recordedMs(a))
}

export function latestTest(cycle: Pick<CycleView, 'gas_tests'>): GasTestView | null {
  return testsNewestFirst(cycle.gas_tests)[0] ?? null
}

export function findGasTest(bundle: Pick<PermitBundle, 'cycles'>, id: string | null | undefined): { test: GasTestView; cycle: CycleView } | null {
  if (!id) return null
  for (const cycle of bundle.cycles) {
    const test = cycle.gas_tests.find((t) => t.id === id)
    if (test) return { test, cycle }
  }
  return null
}

// -----------------------------------------------------------------------------
// Stop resolution at validation. Mirrors private.cyc_stops_to_resolve and the
// STOP_NOT_RESOLVED checks of validate_cycle (0004_cmd_cycle.sql); the server still
// decides.
// -----------------------------------------------------------------------------
export type StopToResolve = { stop: SuspensionView; cycle: CycleView }

/**
 * Every stop the RS must resolve before validating `cycle`: the stops on all earlier
 * cycles from the last validated one (inclusive) up to this one. A cycle that lapsed or
 * ended without a validation does not clear the stop before it, so the immediate
 * predecessor alone is not enough.
 */
export function stopsToResolve(bundle: Pick<PermitBundle, 'cycles'>, cycle: Pick<CycleView, 'cycle_number'>): StopToResolve[] {
  const earlier = bundle.cycles.filter((c) => c.cycle_number < cycle.cycle_number)
  const lastValidated = Math.max(0, ...earlier.filter((c) => c.validation !== null).map((c) => c.cycle_number))
  return earlier
    .filter((c) => c.cycle_number >= lastValidated)
    .sort((a, b) => a.cycle_number - b.cycle_number)
    .flatMap((c) => c.suspensions.map((stop) => ({ stop, cycle: c })))
}

/** A stop that came from an unsafe gas reading (the server counts the reason or a source test). */
export function isGasStop(s: Pick<SuspensionView, 'reason_code' | 'source_gas_test_id'>): boolean {
  return s.reason_code === 'unsafe_gas_result' || s.source_gas_test_id !== null
}

/**
 * Gas tests the RS may cite to resolve the stops: acceptable tests on the cycle being
 * validated (the server accepts no other cycle's test), observed after the latest unsafe
 * reading behind a gas stop - or, when no stop is about gas, after the latest stop.
 * Compared by the tester's observed time, as the server does. Newest first.
 */
export function resolvingGasTests(bundle: Pick<PermitBundle, 'cycles'>, cycle: Pick<CycleView, 'gas_tests'>, stops: readonly StopToResolve[]): GasTestView[] {
  if (stops.length === 0) return []
  const gas = stops.filter((s) => isGasStop(s.stop))
  let after = 0
  for (const { stop } of gas.length > 0 ? gas : stops) {
    const source = findGasTest(bundle, stop.source_gas_test_id)?.test
    after = Math.max(after, parseIsoMs(source?.reported_observed_at ?? stop.reported_observed_at) ?? 0)
  }
  return testsNewestFirst(cycle.gas_tests.filter((t) => t.classification === 'acceptable' && (parseIsoMs(t.reported_observed_at) ?? 0) > after))
}

/**
 * Earlier helper for the previous cycle only, comparing server receipt times. Kept for
 * compatibility; the validation panel uses stopsToResolve and resolvingGasTests, which
 * follow the server: tests on the cycle being validated, by observed time, for every
 * stop since the last validated cycle.
 */
export function resolvingTestCandidates(predecessor: CycleView, cycle: CycleView): GasTestView[] {
  const all = [...predecessor.gas_tests, ...cycle.gas_tests]
  const stops = predecessor.suspensions
  if (stops.length === 0) return []
  const gasStops = stops.filter((s) => s.reason_code === 'unsafe_gas_result')
  let after = 0
  for (const s of gasStops.length > 0 ? gasStops : stops) {
    const source = all.find((t) => t.id === s.source_gas_test_id)
    const at = source ? recordedMs(source) : (parseIsoMs(s.server_received_at) ?? 0)
    after = Math.max(after, at)
  }
  return testsNewestFirst(all.filter((t) => t.classification === 'acceptable' && recordedMs(t) > after))
}

export function talkSignatures(talk: Pick<ToolboxTalkView, 'attendees'>): { signed: number; total: number } {
  return { signed: talk.attendees.filter((a) => a.signed !== null).length, total: talk.attendees.length }
}

export function talksNewestFirst(talks: readonly ToolboxTalkView[]): ToolboxTalkView[] {
  return [...talks].sort((a, b) => (parseIsoMs(b.giver.server_signed_at) ?? 0) - (parseIsoMs(a.giver.server_signed_at) ?? 0))
}

export function findTalk(bundle: PermitBundle, talkId: string | null | undefined): { talk: ToolboxTalkView; cycle: CycleView } | null {
  if (!talkId) return null
  for (const cycle of bundle.cycles) {
    const talk = cycle.toolbox_talks.find((t) => t.id === talkId)
    if (talk) return { talk, cycle }
  }
  return null
}

/**
 * The talk an attendee should sign: the server's talk_id hint, else the newest talk on
 * the current cycle that lists this person and is not yet signed by them.
 */
export function talkForAttendee(
  bundle: PermitBundle,
  personId: string,
  talkId: string | null,
): { talk: ToolboxTalkView; cycle: CycleView; attendee: ToolboxTalkView['attendees'][number] | null } | null {
  const hinted = findTalk(bundle, talkId)
  if (hinted) {
    return { ...hinted, attendee: hinted.talk.attendees.find((a) => a.person.id === personId) ?? null }
  }
  const cycle = findCycle(bundle, bundle.permit.current_cycle_id)
  if (!cycle) return null
  for (const talk of talksNewestFirst(cycle.toolbox_talks)) {
    const attendee = talk.attendees.find((a) => a.person.id === personId)
    if (attendee && attendee.signed === null) return { talk, cycle, attendee }
  }
  return null
}

/**
 * Which confirmations a stop has (display only; the server sets the cycle state). The
 * supervisor's confirmation is the RS's from 0016; an Area Authority confirmation on a record
 * made before 0016 counts the same and is reported as role 'AA' so it can be labelled historic.
 */
export function suspensionConfirmations(s: SuspensionView): { phDeclared: boolean; rsConfirmed: boolean; rsConfirmedAs: 'RS' | 'AA' | null } {
  const phFormal = s.reporter_capacity === 'PH' && s.ph_attestation !== null
  const supervisor = s.confirmations.find((c) => c.confirmation_role === 'RS') ?? s.confirmations.find((c) => c.confirmation_role === 'AA') ?? null
  return {
    phDeclared: phFormal || s.confirmations.some((c) => c.confirmation_role === 'PH'),
    rsConfirmed: supervisor !== null,
    rsConfirmedAs: supervisor ? (supervisor.confirmation_role as 'RS' | 'AA') : null,
  }
}

// -----------------------------------------------------------------------------
// Form completeness (input shape only; business rules stay on the server)
// -----------------------------------------------------------------------------
type CodeOption = { code: string; label: string }

export function missingCodes(options: readonly CodeOption[], value: Record<string, boolean>): CodeOption[] {
  return options.filter((o) => value[o.code] !== true)
}

export function allTicked(options: readonly CodeOption[], value: Record<string, boolean>): Record<string, boolean> {
  return Object.fromEntries(options.map((o) => [o.code, value[o.code] === true]))
}

/** "3", "3 and 7", "3, 7 and 12". */
export function joinNumbers(numbers: readonly number[]): string {
  if (numbers.length <= 1) return numbers.join('')
  return `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

/** Unticked statements: named when there are up to three, otherwise counted (short on a phone). */
export function tickProblems(options: readonly CodeOption[], value: Record<string, boolean>, noun: string): string[] {
  const missing = missingCodes(options, value)
  if (missing.length === 0) return []
  if (missing.length <= 3) return missing.map((o) => `Tick: ${o.label}`)
  return [`Tick every ${noun}: ${missing.length} of ${options.length} not ticked yet.`]
}

export type CheckResult = 'confirmed' | 'not_applicable' | 'prearranged'
export type CheckAnswer = { result: CheckResult | null; note: string }

export function emptyAnswers(options: readonly CodeOption[]): Record<string, CheckAnswer> {
  return Object.fromEntries(options.map((o) => [o.code, { result: null, note: '' }]))
}

/**
 * Every check answered; "Not applicable" needs a note; "Pre-arranged" only for gas.
 * Up to three unanswered checks are named; more are listed by their number.
 */
export function validationCheckProblems(options: readonly CodeOption[], answers: Record<string, CheckAnswer>): string[] {
  const unanswered: number[] = []
  const out: string[] = []
  options.forEach((o, i) => {
    const a = answers[o.code]
    if (!a || a.result === null) unanswered.push(i)
    else if (a.result === 'not_applicable' && !a.note.trim()) out.push(`Say why check ${i + 1} does not apply: ${o.label}`)
    else if (a.result === 'prearranged' && o.code !== 'gas_tests') out.push(`Pre-arranged is only for gas tests: ${o.label}`)
  })
  if (unanswered.length === 0) return out
  if (unanswered.length <= 3) return [...unanswered.map((i) => `Answer check ${i + 1}: ${options[i].label}`), ...out]
  if (unanswered.length === options.length) return [`Answer all ${options.length} checks.`, ...out]
  return [`Answer checks ${joinNumbers(unanswered.map((i) => i + 1))}.`, ...out]
}

/**
 * Answered checks in catalogue order, each code once. An unanswered check is left out
 * (never filled in), so the server reports it as incomplete instead of a confirmation
 * the RS did not give. A note goes only with "Not applicable", the one answer that shows
 * a note box, so text typed before the RS changed the answer is never signed unseen.
 */
export function buildValidationChecks(
  options: readonly CodeOption[],
  answers: Record<string, CheckAnswer>,
): CommandArgs['validate_cycle']['checks'] {
  return options.flatMap((o) => {
    const a = answers[o.code]
    if (!a || a.result === null) return []
    const note = a.result === 'not_applicable' ? a.note.trim() : ''
    return [{ code: o.code, result: a.result, note: note ? note : null }]
  })
}

/** "Oxygen (O2)", "Flammable (LEL)": the code is shown once, even when the label has it. */
export function speciesLabel(code: string): string {
  const s = GAS_SPECIES.find((g) => g.code === code)
  if (!s) return code
  return s.label.includes(`(${s.code})`) ? s.label : `${s.label} (${s.code})`
}

/** A typed reading: accepts a decimal comma; blank or non-numeric is null. */
export function parseReading(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export type ReadingRow = { species_code: string; unit_code: string; value: string; within: boolean | null }

export function readingProblems(rows: readonly ReadingRow[]): string[] {
  const out: string[] = []
  if (rows.length === 0) out.push('Add at least one gas reading.')
  for (const r of rows) {
    const name = speciesLabel(r.species_code)
    if (!r.unit_code) out.push(`Choose the unit for ${name}.`)
    if (parseReading(r.value) === null) out.push(`Enter a number for ${name}.`)
    if (r.within === null) out.push(`Answer "within the referenced criteria?" for ${name}.`)
  }
  return out
}

export function buildMeasurements(rows: readonly ReadingRow[]): CommandArgs['record_gas_test']['measurements'] {
  return rows.map((r) => ({
    species_code: r.species_code,
    unit_code: r.unit_code,
    measured_value: parseReading(r.value) ?? Number.NaN,
    agt_declared_within_criteria: r.within,
  }))
}

/** The unit to preselect: only when the rules allow exactly one unit for the gas. */
export function defaultUnit(units: Record<string, string[]> | null, species: string): string {
  const allowed = units?.[species] ?? []
  return allowed.length === 1 ? allowed[0] : ''
}

/** One empty row per species, using the only allowed unit when there is exactly one. */
export function initialReadingRows(species: readonly string[], units: Record<string, string[]> | null): ReadingRow[] {
  return species.map((code) => ({ species_code: code, unit_code: defaultUnit(units, code), value: '', within: null }))
}

/** Rows with a blank unit take the single allowed unit, if there is one. */
export function withDefaultUnits(rows: readonly ReadingRow[], units: Record<string, string[]> | null): ReadingRow[] {
  return rows.map((r) => (r.unit_code ? r : { ...r, unit_code: defaultUnit(units, r.species_code) }))
}

export function textOf(value: unknown): string {
  return typeof value === 'string' ? value : ''
}
